import { chakra } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import { useMemo } from 'react';

import Dot from '@/components/Dot/Dot';
import URLs from '@/enums/urls';
import useStatusPage from '@/hooks/useStatusPage';

import withQueryClientProvider from '../withQueryClientProvider/withQueryClientProvider';

export type BitriseStatusBadgeProps = {
  description: string;
  severity?: 'none' | 'minor' | 'major' | 'critical' | string;
};

export const BitriseStatusBadge = ({ description, severity }: BitriseStatusBadgeProps) => {
  const backgroundColor = useMemo<string>(() => {
    switch (severity) {
      case 'critical':
        return 'color/red/bold';
      case 'major':
        return 'color/orange/bold';
      case 'minor':
        return 'color/yellow/muted';
      case 'none':
      default:
        return 'color/green/bold';
    }
  }, [severity]);

  return (
    <chakra.a
      display="inline-flex"
      alignItems="center"
      gap="8"
      borderRadius="4"
      backgroundColor="background/tertiary"
      paddingBlock="4"
      paddingInline="8"
      href={URLs.STATUS_PAGE}
      rel="noreferrer"
      target="_blank"
      color="text/secondary"
    >
      <Dot size="8" backgroundColor={backgroundColor} />
      <Text textStyle="comp/badge/sm">{description}</Text>
    </chakra.a>
  );
};

export const BitriseStatusContent = () => {
  const { data: status } = useStatusPage();

  const severity = useMemo<BitriseStatusBadgeProps['severity']>(
    () => (status?.indicator === 'maintenance' ? 'minor' : status?.indicator),
    [status?.indicator],
  );

  if (!status) {
    return null;
  }
  return <BitriseStatusBadge description={status.description} severity={severity} />;
};

export default withQueryClientProvider(BitriseStatusContent);
